import { keepPreviousData, useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { getActingTenantId } from "../auth/session";
import type { Device, DeviceConfiguration, ListResponse, SingleResponse } from "./types";
import { setDayRangeParams, type DayRange } from "../lib/dayRange";

export type DeviceSearchField = "imei" | "deviceName" | "contactNumber" | "model" | "firmwareVersion";

// Server-side filters for the tenant device list. They apply across every
// page — never filter a page of this list in the browser, or matches on
// later pages silently disappear. `status` is the derived status the list
// shows: online, stale or offline.
export interface DeviceFilters {
  search?: string;
  searchField?: DeviceSearchField;
  status?: string;
  groupId?: string;
  unallocated?: boolean;
  // Local days, sent as the instants that bound them.
  created?: DayRange;
  lastSeen?: DayRange;
}

interface ListDevicesParams extends DeviceFilters {
  limit?: number;
  cursor?: string;
  tenantId?: string;
}

// The device lists' "More filters" day pickers, keyed by device field.
export const DEVICE_DATE_RANGE_FILTERS = [
  { key: "createdAt", label: "Created" },
  { key: "lastSeen", label: "Last seen" },
];

// Maps DataTable's date ranges (keyed as above) onto the list filters.
export function deviceDateFilters(ranges: Record<string, DayRange> = {}): Pick<DeviceFilters, "created" | "lastSeen"> {
  return { created: ranges.createdAt, lastSeen: ranges.lastSeen };
}

export function deviceFilterParams(filters: DeviceFilters = {}): URLSearchParams {
  const qs = new URLSearchParams();
  const search = filters.search?.trim();
  if (search) {
    qs.set("search", search);
    qs.set("searchField", filters.searchField ?? "imei");
  }
  if (filters.status) qs.set("status", filters.status);
  if (filters.groupId) qs.set("groupId", filters.groupId);
  if (filters.unallocated) qs.set("unallocated", "true");
  setDayRangeParams(qs, "created", filters.created);
  setDayRangeParams(qs, "lastSeen", filters.lastSeen);
  return qs;
}

interface TenantScopedQueryOptions {
  useActingTenantFallback?: boolean;
  requireTenant?: boolean;
  enabled?: boolean;
  // Poll interval in ms. The gateway settings page polls its configuration so
  // the reported (on-device) side updates while an operator waits for a
  // check-in.
  refetchInterval?: number;
}

export function useDevices(params: ListDevicesParams = {}, options: Pick<TenantScopedQueryOptions, "enabled"> = {}) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = params.tenantId ?? actingTenantId ?? undefined;
  const qs = deviceFilterParams(params);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString();

  return useQuery({
    queryKey: ["devices", scopeTenantId, params],
    queryFn: () => apiFetch<ListResponse<Device>>(`/api/v1/devices${query ? `?${query}` : ""}`, {
      headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
    }),
    // Keep the previous page visible while a new filter/cursor is
    // being fetched. Without this the whole DataTable unmounts on
    // every filter change and its internal state (selected status,
    // current search text) resets — see §4.9 in coding-standards.md.
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

// How many devices match `filters` across every page (countOnly). Kept apart
// from the page query so paging doesn't recount, and so the table can render
// while the count is still in flight.
export function useDeviceTotal(
  filters: DeviceFilters & { tenantId?: string } = {},
  options: Pick<TenantScopedQueryOptions, "enabled"> = {},
) {
  const scopeTenantId = filters.tenantId ?? getActingTenantId() ?? undefined;
  const qs = deviceFilterParams(filters);
  const filterKey = qs.toString();
  qs.set("countOnly", "true");

  return useQuery({
    queryKey: ["devices", scopeTenantId, "total", filterKey],
    queryFn: async () => {
      const res = await apiFetch<ListResponse<Device>>(`/api/v1/devices?${qs.toString()}`, {
        headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
      });
      return { total: res.pagination.total ?? 0, isLowerBound: res.pagination.totalIsLowerBound ?? false };
    },
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

export function useDevice(imei: string) {
  return useDeviceForTenant(imei);
}

export function useDeviceForTenant(imei: string, tenantId?: string, options: TenantScopedQueryOptions = {}) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = tenantId ?? (options.useActingTenantFallback === false ? undefined : actingTenantId) ?? undefined;
  return useQuery({
    queryKey: ["device", scopeTenantId ?? null, imei],
    queryFn: () => apiFetch<SingleResponse<Device>>(`/api/v1/devices/${imei}`, {
      headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
    }),
    enabled: !!imei && (!options.requireTenant || !!scopeTenantId),
    // The detail page is where an operator watches a device through an
    // update — poll so status/firmware reflect backend changes without a
    // manual reload (the list relies on staleTime + navigation instead).
    refetchInterval: 30_000,
  });
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      imei: string;
      deviceName?: string;
      contactNumber?: string;
      deviceTypeId?: string;
      model?: string;
      description?: string;
      groupId?: string;
    }) =>
      apiFetch<SingleResponse<Device>>("/api/v1/devices", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["devices"] }),
  });
}

export function useAdminCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, body }: {
      tenantId: string;
      body: {
        imei: string;
        deviceName?: string;
        contactNumber?: string;
        deviceTypeId?: string;
        model?: string;
        description?: string;
        groupId?: string;
      };
    }) =>
      apiFetch<SingleResponse<Device>>("/api/v1/devices", {
        method: "POST",
        headers: { "X-Acting-Tenant-ID": tenantId },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      qc.invalidateQueries({ queryKey: ["devices"] });
    },
  });
}

export function useAdminUpdateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, imei, body }: {
      tenantId: string;
      imei: string;
      body: UpdateDeviceRequest;
    }) =>
      apiFetch<SingleResponse<Device>>(`/api/v1/devices/${imei}`, {
        method: "PUT",
        headers: { "X-Acting-Tenant-ID": tenantId },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device"] });
    },
  });
}

export function useAdminDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, imei }: { tenantId: string; imei: string }) =>
      apiFetch<void>(`/api/v1/devices/${imei}`, {
        method: "DELETE",
        headers: { "X-Acting-Tenant-ID": tenantId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device"] });
    },
  });
}

export interface UpdateDeviceRequest {
  imei?: string;
  deviceName?: string;
  contactNumber?: string | null;
  deviceTypeId?: string | null;
  model?: string | null;
  description?: string;
  groupId?: string | null;
  tags?: string[];
}

export function useUpdateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imei, body }: { imei: string; body: UpdateDeviceRequest }) =>
      apiFetch<SingleResponse<Device>>(`/api/v1/devices/${imei}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device"] });
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (imei: string) =>
      apiFetch<void>(`/api/v1/devices/${imei}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device"] });
    },
  });
}

export function usePatchDeviceConfiguration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imei, overrides, tenantId }: { imei: string; overrides: Record<string, unknown>; tenantId?: string }) =>
      apiFetch<SingleResponse<DeviceConfiguration>>(`/api/v1/devices/${imei}/configuration`, {
        method: "PATCH",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify({ overrides }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["device"] });
      qc.invalidateQueries({ queryKey: ["devices"] });
      qc.invalidateQueries({ queryKey: ["device-configuration"] });
    },
  });
}

export function useDeviceConfiguration(imei: string) {
  return useDeviceConfigurationForTenant(imei);
}

export function useDeviceConfigurationForTenant(imei: string, tenantId?: string, options: TenantScopedQueryOptions = {}) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = tenantId ?? (options.useActingTenantFallback === false ? undefined : actingTenantId) ?? undefined;
  return useQuery({
    queryKey: ["device-configuration", scopeTenantId ?? null, imei],
    queryFn: () => apiFetch<SingleResponse<DeviceConfiguration>>(`/api/v1/devices/${imei}/configuration`, {
      headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
    }),
    enabled: !!imei && (!options.requireTenant || !!scopeTenantId),
    refetchInterval: options.refetchInterval,
  });
}

// A task that targeted a device, with the device's own progress in it
// (GET /api/v1/devices/{imei}/tasks, served by api-tasks).
export interface DeviceTaskSummary {
  taskId: string;
  name: string;
  taskType?: string;
  status: string;
  firmwareId?: string;
  rolloutStrategy: string;
  createdAt: string;
  deviceStatus: string;
  deviceStartedAt?: string;
  deviceCompletedAt?: string;
  deviceErrorMessage?: string;
  deviceAttempts?: number;
}

// A device's task history, newest task first, a page at a time ("Load
// older tasks" fetches the next).
export function useDeviceTasks(imei: string) {
  const actingTenantId = getActingTenantId();
  return useInfiniteQuery({
    queryKey: ["device-tasks", actingTenantId, imei],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (pageParam) qs.set("cursor", pageParam);
      const query = qs.toString();
      return apiFetch<ListResponse<DeviceTaskSummary>>(
        `/api/v1/devices/${encodeURIComponent(imei)}/tasks${query ? `?${query}` : ""}`,
      );
    },
    initialPageParam: "",
    getNextPageParam: (last) => (last.pagination?.hasMore ? last.pagination.cursor : undefined),
    enabled: !!imei,
  });
}

// DeviceEvent is one entry in a device's activity log
// (GET /api/v1/devices/{imei}/history), newest first.
export interface DeviceEvent {
  type: string;
  message?: string;
  detail?: Record<string, string>;
  timestamp: string;
}

// historyPollInterval: poll while only the newest page is loaded. Once an
// operator has paged back through older activity, polling would refetch
// every loaded page each tick, so it stops (refresh by reopening the log).
export function historyPollInterval(loadedPages: number, interval: number): number | false {
  if (interval <= 0 || loadedPages > 1) return false;
  return interval;
}

// useDeviceHistory streams a device's activity a page at a time, newest
// first: fetchNextPage appends the next older page. It polls so the log
// updates live while an operator watches a device through an update; pass
// refetchInterval: 0 to disable.
export function useDeviceHistory(imei: string, options: { limit?: number; refetchInterval?: number } = {}) {
  const actingTenantId = getActingTenantId();
  return useInfiniteQuery({
    queryKey: ["device-history", actingTenantId, imei, options.limit ?? null],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (options.limit) qs.set("limit", String(options.limit));
      if (pageParam) qs.set("cursor", pageParam);
      const query = qs.toString();
      return apiFetch<ListResponse<DeviceEvent>>(`/api/v1/devices/${imei}/history${query ? `?${query}` : ""}`, {
        headers: actingTenantId ? { "X-Acting-Tenant-ID": actingTenantId } : undefined,
      });
    },
    initialPageParam: "",
    getNextPageParam: (last) => (last.pagination?.hasMore ? last.pagination.cursor : undefined),
    enabled: !!imei,
    refetchInterval: (query) => historyPollInterval(query.state.data?.pages.length ?? 0, options.refetchInterval ?? 15_000),
  });
}
